import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const STORAGE_KEY = "wmt-initial-utm";

type UtmValues = Record<string, string>;

const readUtmValues = (search: string): UtmValues => {
  const values: UtmValues = {};
  new URLSearchParams(search).forEach((value, key) => {
    const normalizedKey = key.toLowerCase();
    if (normalizedKey.startsWith("utm_") && value) values[normalizedKey] = value;
  });
  return values;
};

const readStoredUtmValues = (): UtmValues => {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) as UtmValues : {};
  } catch {
    return {};
  }
};

const storeUtmValues = (values: UtmValues) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // The URL still keeps the UTM values when session storage is unavailable.
  }
};

const UtmPersistence = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentUtmValues = readUtmValues(location.search);
    let initialUtmValues = readStoredUtmValues();

    if (Object.keys(initialUtmValues).length === 0 && Object.keys(currentUtmValues).length > 0) {
      initialUtmValues = currentUtmValues;
      storeUtmValues(initialUtmValues);
    }

    if (Object.keys(initialUtmValues).length === 0) return;

    const searchParams = new URLSearchParams(location.search);
    [...searchParams.keys()].forEach((key) => {
      if (key.toLowerCase().startsWith("utm_")) searchParams.delete(key);
    });
    Object.entries(initialUtmValues).forEach(([key, value]) => searchParams.set(key, value));

    const nextSearch = searchParams.toString();
    const normalizedSearch = nextSearch ? `?${nextSearch}` : "";
    if (normalizedSearch === location.search) return;

    navigate(
      {
        pathname: location.pathname,
        search: normalizedSearch,
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  return null;
};

export default UtmPersistence;
